import { clsx } from 'clsx';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';
import { Game } from '../types';

interface GameCardProps {
  game: Game;
  selectedPick?: 'home' | 'away';
  onPickChange?: (pick: 'home' | 'away') => void;
  disabled?: boolean;
  isFinalGame?: boolean;
}

const GameCard = ({ game, selectedPick, onPickChange, disabled = false, isFinalGame = false }: GameCardProps) => {
  const gameTime = new Date(game.startTime);
  const hasStarted = gameTime <= new Date() || game.status !== 'scheduled';
  
  const getStatusDisplay = () => {
    switch (game.status) {
      case 'final':
        return `Final: ${game.homeScore || 0} - ${game.awayScore || 0}`;
      case 'in_progress':
        return 'In Progress';
      case 'postponed':
        return 'Postponed';
      default:
        return format(gameTime, 'h:mm a');
    }
  };

  const getWinnerDisplay = () => {
    if (game.status === 'final' && game.winner) {
      return game.winner === 'home' ? game.homeTeam : game.awayTeam;
    }
    return null;
  };

  return (
    <div className={clsx(
      "bg-white rounded-lg border-2 p-4 transition-all",
      {
        "border-gray-200 hover:border-gray-300": !selectedPick && !hasStarted && !disabled,
        "border-blue-500 bg-blue-50": selectedPick && !hasStarted,
        "border-gray-300 bg-gray-50": hasStarted || disabled,
        "border-yellow-400 bg-yellow-50": isFinalGame && !hasStarted,
        "ring-2 ring-yellow-300": isFinalGame && !hasStarted
      }
    )}>
      {/* Game Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center text-xs text-gray-500">
          <Clock className="h-3 w-3 mr-1" />
          {getStatusDisplay()}
        </div>
        {isFinalGame && (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
            Tie-breaker
          </span>
        )}
      </div>

      {/* Teams */}
      <div className="space-y-2">
        {/* Away Team */}
        <button
          type="button"
          onClick={() => !disabled && !hasStarted && onPickChange?.('away')}
          disabled={disabled || hasStarted}
          className={clsx(
            "w-full p-2 rounded border text-left transition-all",
            {
              "hover:bg-gray-50 border-gray-200": !selectedPick && !hasStarted && !disabled,
              "bg-blue-100 border-blue-300": selectedPick === 'away' && !hasStarted,
              "bg-gray-100 border-gray-200 cursor-not-allowed": hasStarted || disabled,
              "bg-green-100 border-green-300": game.status === 'final' && game.winner === 'away',
            }
          )}
        >
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm">{game.awayTeam}</div>
            {game.status === 'final' && (
              <div className="text-sm font-mono">{game.awayScore}</div>
            )}
          </div>
          <div className="text-xs text-gray-500">@ {game.homeTeam}</div>
        </button>

        {/* Home Team */}
        <button
          type="button"
          onClick={() => !disabled && !hasStarted && onPickChange?.('home')}
          disabled={disabled || hasStarted}
          className={clsx(
            "w-full p-2 rounded border text-left transition-all",
            {
              "hover:bg-gray-50 border-gray-200": !selectedPick && !hasStarted && !disabled,
              "bg-blue-100 border-blue-300": selectedPick === 'home' && !hasStarted,
              "bg-gray-100 border-gray-200 cursor-not-allowed": hasStarted || disabled,
              "bg-green-100 border-green-300": game.status === 'final' && game.winner === 'home',
            }
          )}
        >
          <div className="flex items-center justify-between">
            <div className="font-medium text-sm">{game.homeTeam}</div>
            {game.status === 'final' && (
              <div className="text-sm font-mono">{game.homeScore}</div>
            )}
          </div>
          <div className="text-xs text-gray-500">Home</div>
        </button>
      </div>

      {/* Winner Display */}
      {game.status === 'final' && getWinnerDisplay() && (
        <div className="mt-2 text-center text-xs text-green-600 font-medium">
          Winner: {getWinnerDisplay()}
        </div>
      )}

      {/* Selection Indicator */}
      {selectedPick && !hasStarted && (
        <div className="mt-2 text-center text-xs text-blue-600 font-medium">
          Selected: {selectedPick === 'home' ? game.homeTeam : game.awayTeam}
        </div>
      )}
    </div>
  );
};

export default GameCard;
